import React, {useCallback, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';

import useOnResize from 'lib/hooks/useOnResize';

import {AnnotationIndicatorHost} from '../AnnotationNoteIndicators/AnnotationIndicatorHost';
import AnnotationNoteIndicator from '../AnnotationNoteIndicators/AnnotationNoteIndicator';
import {
  hasRenderedBox,
  resolveIndicatorScroller,
  resolveIndicatorSibling,
  scrollerContentOffset,
  selfAndAncestorsThrough
} from '../engine/annotationIndicatorPlacement';
import type {Annotation, HighlightColor} from '../types';

const NO_TARGETS: Element[] = [];

interface AnnotationSupplementNoteIndicatorProps {
  /**
   * The annotatable supplement's element: the indicator lines up with its top and
   * portals into the pane that scrolls it. Resolved from here rather than from the
   * card, because a type renderer can wrap its card in an overflow container of its
   * own, which would capture the indicator inside the supplement.
   */
  region: HTMLElement | null;
  /** Every mark on the supplement carrying a note, in the order they read. */
  notedMarks: Annotation[];
  /** Follows the marks painted in the language on screen, not every note counted. */
  color: HighlightColor;
}

interface Placement {
  scroller: HTMLElement;
  /** What the indicator reads just before; null leaves it at the pane's end. */
  sibling: Element | null;
  top: number;
}

/**
 * One margin button for everything noted on a supplement. Its marks share no line box
 * to sit beside, so it hangs from the supplement's top and reads just before it.
 * Like a text mark's button, it hides while the supplement lays out no box, as inside
 * a collapsed prompt.
 */
export default function AnnotationSupplementNoteIndicator({
  region,
  notedMarks,
  color
}: AnnotationSupplementNoteIndicatorProps) {
  const [placement, setPlacement] = useState<Placement | null>(null);

  const measure = useCallback(() => {
    const scroller = notedMarks.length > 0 && resolveIndicatorScroller(region);
    if (!region || !scroller || !hasRenderedBox(region)) {
      setPlacement(null);
      return;
    }
    setPlacement({
      scroller,
      sibling: resolveIndicatorSibling(region, scroller),
      top: region.getBoundingClientRect().top + scrollerContentOffset(scroller)
    });
  }, [region, notedMarks]);

  // The supplement's top moves when anything between it and the pane changes height.
  const resizeTargets = useMemo(() => {
    const scroller = resolveIndicatorScroller(region);
    return region && scroller ? selfAndAncestorsThrough(region, scroller) : NO_TARGETS;
  }, [region]);

  useOnResize(resizeTargets, measure);

  if (!placement || notedMarks.length === 0) {
    return null;
  }
  const indicator = (
    <AnnotationNoteIndicator
      align='top'
      color={color}
      line={{center: placement.top, annotations: notedMarks}}
    />
  );
  return placement.sibling ? (
    <AnnotationIndicatorHost sibling={placement.sibling} side='before' pane={placement.scroller}>
      {indicator}
    </AnnotationIndicatorHost>
  ) : (
    createPortal(indicator, placement.scroller)
  );
}
